import axios from 'axios';
import { from, Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { embedDashboard } from "@superset-ui/embedded-sdk";

// const supersetUrl = 'http://74.225.216.234:8088';
const supersetUrl = 'https://analytics.ciana.health';
const supersetApiUrl = `${supersetUrl}/api/v1/security`;

const SupersetService = {
    /**
     * Fetches the token required for embedding the dashboard.
     * @param {string} dashboardId - The dashboard ID.
     */
    getToken(dashboardId) {
        const loginBody = {
            username: 'ciana_admin',
            password: 'Ciana@2025!#',
            provider: 'db',
            refresh: true,
        };

        const loginHeaders = {
            'Content-Type': 'application/json',
        };

        // Only fetch the token if it's not in session storage
        return from(
            axios.post(`${supersetApiUrl}/login`, loginBody, { headers: loginHeaders })
        ).pipe(
            switchMap((response) => {
                const accessToken = response.data.access_token;

                // Store access token in sessionStorage
                sessionStorage.setItem('accessToken', accessToken);

                const guestTokenBody = {
                    resources: [
                        {
                            type: 'dashboard',
                            id: dashboardId,
                        },
                    ],
                    rls: [],
                    user: {
                        username: 'report-viewer',
                        first_name: 'report-viewer',
                        last_name: 'report-viewer',
                    },
                };

                const guestTokenHeaders = {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                };

                return from(
                    axios.post(`${supersetApiUrl}/guest_token/`, guestTokenBody, {
                        headers: guestTokenHeaders,
                    })
                );
            }),
            catchError((error) => {
                console.error(error);
                throw error; // Rethrow error for higher-level handling
            })
        );
    },

    /**
     * Embeds the Superset dashboard using the guest token.
     * @param {string} dashboardId - The ID of the dashboard.
     * @returns {Observable} - An Observable for the embedding process.
     */
    embedDashboard(dashboardId) {
        return new Observable((observer) => {
            // Check if the access token already exists in sessionStorage
            const accessToken = sessionStorage.getItem('accessToken');
            if (accessToken) {
                // If token exists, directly use it to generate guest token
                const guestTokenBody = {
                    resources: [
                        {
                            type: 'dashboard',
                            id: dashboardId,
                        },
                    ],
                    rls: [],
                    user: {
                        username: 'report-viewer',
                        first_name: 'report-viewer',
                        last_name: 'report-viewer',
                    },
                };

                const guestTokenHeaders = {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                };

                from(
                    axios.post(`${supersetApiUrl}/guest_token/`, guestTokenBody, {
                        headers: guestTokenHeaders,
                    })
                ).subscribe(
                    (response) => {
                        const token = response.data.token;

                        embedDashboard({
                            id: dashboardId,
                            supersetDomain: supersetUrl,
                            mountPoint: document.getElementById('superset_embedding_div_class'),
                            fetchGuestToken: () => token,
                            dashboardUiConfig: {
                                hideTitle: true,
                                hideChartControls: true,
                                hideTab: true,
                                filters: {
                                    visible: false,
                                    expanded: false,
                                },
                                urlParams: {
                                    standalone: '1',
                                    show_filters: '0',
                                    show_native_filters: '0',
                                },
                            },
                        });

                        observer.next(); // Notify success
                        observer.complete();
                    },
                    (error) => {
                        observer.error(error); // Notify error
                    }
                );
            } else {
                // If the token does not exist, call getToken to fetch and store it
                this.getToken(dashboardId).subscribe(
                    (response) => {
                        const token = response.data.token;

                        embedDashboard({
                            id: dashboardId,
                            supersetDomain: supersetUrl,
                            mountPoint: document.getElementById('superset_embedding_div_class'),
                            fetchGuestToken: () => token,
                            dashboardUiConfig: {
                                hideTitle: true,
                                hideChartControls: true,
                                hideTab: true,
                                filters: {
                                    visible: false,
                                    expanded: false,
                                },
                                urlParams: {
                                    standalone: '1',
                                    show_filters: '0',
                                    show_native_filters: '0',
                                },
                            },
                        });

                        observer.next(); // Notify success
                        observer.complete();
                    },
                    (error) => {
                        observer.error(error); // Notify error
                    }
                );
            }
        });
    },

    // Fetch all available charts
    fetchCharts() {
        return new Observable((observer) => {
            const accessToken = sessionStorage.getItem('accessToken');
            if (!accessToken) {
                observer.error('No access token found. Please log in first.');
                return;
            }

            console.log("Access Token:", accessToken);  // Debugging token

            axios
                .get(`${supersetUrl}/api/v1/chart/`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    withCredentials: true, // Ensures cookies are included
                })
                .then((response) => {
                    if (!response.data?.result || response.data.result.length === 0) {
                        observer.error('Invalid API response: No charts found.');
                        return;
                    }

                    this.charts = response.data.result;
                    console.log('Fetched Charts:', this.charts); // Debugging API response
                    observer.next(this.charts);
                    observer.complete();
                })
                .catch((error) => {
                    console.error('API Error:', error.response ? error.response.data : error.message);
                    observer.error(error);
                });
        });
    },

    // Load chart data based on chart ID
    loadChartData(chartId) {
        return new Observable((observer) => {
            const accessToken = sessionStorage.getItem('accessToken');

            if (!accessToken) {
                observer.error('No access token found.');
                return;
            }

            axios
                .get(`${supersetUrl}/api/v1/chart/${chartId}`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    withCredentials: true,
                })
                .then((response) => {
                    if (!response.data?.result) {
                        observer.error('Invalid chart data received.');
                        return;
                    }

                    console.log('Loaded Chart Data:', response.data.result);
                    observer.next(response.data.result);
                    observer.complete();
                })
                .catch((error) => {
                    console.error('Error loading chart data:', error.response ? error.response.data : error.message);
                    observer.error(error);
                });
        });
    }
};

export default SupersetService;
